// ============================================================================
// BIMPrimitives.js
// All geometry helpers + textures + materials used by <ThreeViewport />.
// Kept free of React so they can be reused from anywhere (snapshots, PDF
// exporters, unit tests).
// ============================================================================
import * as THREE from "three";

// ============================================================================
// Procedural textures
// ============================================================================
export function makeSidingTexture(repeat = 1) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#f7f3ec";
  ctx.fillRect(0, 0, 256, 256);
  ctx.strokeStyle = "rgba(0,0,0,0.08)";
  ctx.lineWidth = 1;
  for (let y = 0; y < 256; y += 16) {
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(256, y + 0.5);
    ctx.stroke();
    ctx.fillStyle = "rgba(0,0,0,0.04)";
    ctx.fillRect(0, y, 256, 2);
  }
  for (let i = 0; i < 1200; i++) {
    ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.04})`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 1, 1);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeMetalRoofTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  const grad = ctx.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0, "#1f2429");
  grad.addColorStop(1, "#2c333a");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 2;
  for (let x = 0; x < 256; x += 32) {
    ctx.beginPath();
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, 256);
    ctx.stroke();
  }
  ctx.strokeStyle = "rgba(255,255,255,0.05)";
  for (let x = 4; x < 256; x += 32) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, 256);
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeGrassTexture() {
  const c = document.createElement("canvas");
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#3a4a2a";
  ctx.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 1500; i++) {
    const x = Math.random() * 128;
    const y = Math.random() * 128;
    const v = 0.4 + Math.random() * 0.6;
    ctx.fillStyle = `rgba(${60 + v * 30}, ${90 + v * 60}, ${30 + v * 30}, ${0.6 + v * 0.4})`;
    ctx.fillRect(x, y, 1, 1 + Math.random());
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(40, 40);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeStairsTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#3d3a36";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 12; i++) {
    const y = i * 21;
    ctx.fillStyle = i % 2 === 0 ? "#4a463f" : "#36332f";
    ctx.fillRect(0, y, 256, 21);
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(256, y + 0.5);
    ctx.stroke();
  }
  for (let i = 0; i < 800; i++) {
    ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.05})`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 1, 1);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeBrickTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#5b3527";
  ctx.fillRect(0, 0, 256, 256);
  const bw = 64, bh = 22;
  for (let y = 0; y < 256; y += bh) {
    const row = Math.floor(y / bh);
    const offset = (row % 2) * (bw / 2);
    for (let x = -bw; x < 256 + bw; x += bw) {
      const r = 90 + Math.floor(Math.random() * 30);
      const g = 50 + Math.floor(Math.random() * 15);
      const b = 40 + Math.floor(Math.random() * 12);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(x + offset + 1, y + 1, bw - 2, bh - 2);
      ctx.strokeStyle = "rgba(0,0,0,0.4)";
      ctx.strokeRect(x + offset + 1, y + 1, bw - 2, bh - 2);
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeStoneTexture() {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#7d7a72";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * 256;
    const y = Math.random() * 256;
    const w = 30 + Math.random() * 50;
    const h = 18 + Math.random() * 28;
    const v = 90 + Math.floor(Math.random() * 50);
    ctx.fillStyle = `rgb(${v},${v - 5},${v - 12})`;
    ctx.beginPath();
    ctx.ellipse(x, y, w / 2, h / 2, Math.random() * Math.PI, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeWoodPlankTexture() {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#7a5a36";
  ctx.fillRect(0, 0, 256, 256);
  for (let x = 0; x < 256; x += 32) {
    const r = 110 + Math.floor(Math.random() * 25);
    const g = 80 + Math.floor(Math.random() * 18);
    const b = 50 + Math.floor(Math.random() * 14);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(x, 0, 32, 256);
    ctx.strokeStyle = "rgba(0,0,0,0.45)";
    ctx.beginPath();
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, 256);
    ctx.stroke();
  }
  for (let i = 0; i < 3000; i++) {
    ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.07})`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 1, 1);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeConcreteTexture() {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#a8a8a3";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 4000; i++) {
    const v = Math.random();
    ctx.fillStyle = `rgba(${100 + v * 40},${100 + v * 40},${95 + v * 40},${0.2 + v * 0.3})`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 1 + Math.random(), 1 + Math.random());
  }
  ctx.strokeStyle = "rgba(40,40,40,0.45)";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(0, 128); ctx.lineTo(256, 128);
  ctx.moveTo(128, 0); ctx.lineTo(128, 256);
  ctx.stroke();
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// ============================================================================
// Material helpers + color maps
// ============================================================================
export const ROOF_COLOR_MAP = {
  shingle: 0x3a3f44,
  metal: 0x4f5d6b,
  tile: 0x8a3a2a,
  membrane: 0x222222,
};

export const WALL_COLOR_MAP = {
  concrete: 0xb0b3b8,
  brick: 0x9a4a3a,
  wood: 0x7a5236,
  glass: 0x88c0e0,
  stucco: 0xece4d3,
  steel: 0x90a0b2,
  siding: 0xf7f3ec,
};

export function isFarmhouseStyle(blueprint, fallbackStyle) {
  const s = blueprint?.metadata?.style || fallbackStyle || "";
  return String(s).toLowerCase().includes("farmhouse");
}

export function buildWallMaterial({ blueprint, wall, sidingTexture, style, isBasement = false, concreteTexture = null }) {
  if (isBasement) {
    if (concreteTexture) {
      const tex = concreteTexture.clone();
      tex.needsUpdate = true;
      tex.repeat.set(2, 1.5);
      return new THREE.MeshStandardMaterial({
        map: tex,
        color: 0xbfc2c6,
        roughness: 0.95,
        metalness: 0.02,
      });
    }
    return new THREE.MeshStandardMaterial({
      color: 0xa8acb0,
      roughness: 0.95,
      metalness: 0.02,
    });
  }
  const farmhouse = isFarmhouseStyle(blueprint, style);
  if (farmhouse) {
    return new THREE.MeshStandardMaterial({
      map: sidingTexture,
      color: 0xffffff,
      roughness: 0.85,
      metalness: 0.0,
    });
  }
  const matName = (wall.material || "").toLowerCase();
  const color = WALL_COLOR_MAP[matName] ?? 0xbfc4ca;
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.7,
    metalness: 0.05,
  });
}

export function buildRoofMaterial({ blueprint, roof, metalRoofTexture, style }) {
  const farmhouse = isFarmhouseStyle(blueprint, style);
  if (farmhouse) {
    return new THREE.MeshStandardMaterial({
      map: metalRoofTexture,
      color: 0xffffff,
      roughness: 0.55,
      metalness: 0.55,
      side: THREE.DoubleSide,
    });
  }
  const matName = (roof?.material || blueprint?.materials?.roof || "shingle").toLowerCase();
  const color = ROOF_COLOR_MAP[matName] ?? 0x3a3f44;
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.85,
    metalness: 0.1,
    side: THREE.DoubleSide,
  });
}

// ============================================================================
// Dormer (small gabled projection on the main roof slope)
// ============================================================================
export function buildDormer({ group, dormer, roofMeta, materialFactory }) {
  const { type, longerIsX, span, ridgeLen, cx, cz, eaveY } = roofMeta;
  if (type !== "gable") return;
  const face = String(dormer.roof_face || "south").toLowerCase();
  const pos = Math.max(0, Math.min(1, Number(dormer.position ?? 0.5)));
  const w = Number(dormer.width || 1.6);
  const projection = Number(dormer.projection || 1.2);
  const dh = Number(dormer.height || 1.6);

  let center = new THREE.Vector3(cx, eaveY, cz);
  const half = ridgeLen / 2;
  if (longerIsX) {
    center.x = cx - half + pos * ridgeLen;
    if (face === "south") center.z = cz + span / 2 - projection / 2 - 0.2;
    else if (face === "north") center.z = cz - span / 2 + projection / 2 + 0.2;
    else return;
  } else {
    center.z = cz - half + pos * ridgeLen;
    if (face === "east") center.x = cx + span / 2 - projection / 2 - 0.2;
    else if (face === "west") center.x = cx - span / 2 + projection / 2 + 0.2;
    else return;
  }

  const outward = (() => {
    if (longerIsX && face === "south") return new THREE.Vector3(0, 0, 1);
    if (longerIsX && face === "north") return new THREE.Vector3(0, 0, -1);
    if (!longerIsX && face === "east") return new THREE.Vector3(1, 0, 0);
    return new THREE.Vector3(-1, 0, 0);
  })();
  const yawRad = Math.atan2(outward.x, outward.z);

  const wallMatName = (roofMeta.blueprint?.materials?.wall || "siding").toLowerCase();
  const farmhouse = isFarmhouseStyle(roofMeta.blueprint);
  const wallMat = farmhouse
    ? new THREE.MeshStandardMaterial({
        map: roofMeta.sidingTexture, color: 0xffffff, roughness: 0.85, metalness: 0.0,
      })
    : new THREE.MeshStandardMaterial({
        color: WALL_COLOR_MAP[wallMatName] ?? 0xeae0d3, roughness: 0.8,
      });

  const winSpec = dormer.window || { width: w * 0.7, height: dh * 0.6, sill_height: 0.3 };
  const ww = Number(winSpec.width || w * 0.7);
  const wh = Number(winSpec.height || dh * 0.6);
  const sill = Number(winSpec.sill_height ?? 0.3);

  const dormerGroup = new THREE.Group();
  const frontShape = new THREE.Shape();
  frontShape.moveTo(-w / 2, 0);
  frontShape.lineTo(w / 2, 0);
  frontShape.lineTo(w / 2, dh);
  frontShape.lineTo(-w / 2, dh);
  frontShape.lineTo(-w / 2, 0);
  const winHole = new THREE.Path();
  winHole.moveTo(-ww / 2, sill);
  winHole.lineTo(ww / 2, sill);
  winHole.lineTo(ww / 2, sill + wh);
  winHole.lineTo(-ww / 2, sill + wh);
  winHole.lineTo(-ww / 2, sill);
  frontShape.holes.push(winHole);
  const frontGeo = new THREE.ExtrudeGeometry(frontShape, { depth: 0.08, bevelEnabled: false });
  frontGeo.computeVertexNormals();
  const front = new THREE.Mesh(frontGeo, wallMat);
  front.castShadow = true;
  front.receiveShadow = true;
  front.position.set(0, 0, projection / 2);
  dormerGroup.add(front);

  const pane = new THREE.Mesh(
    new THREE.PlaneGeometry(ww, wh),
    new THREE.MeshPhysicalMaterial({
      color: 0x88c0e0, transparent: true, opacity: 0.4,
      roughness: 0.05, transmission: 0.6, metalness: 0.0,
    }),
  );
  pane.position.set(0, sill + wh / 2, projection / 2 + 0.05);
  dormerGroup.add(pane);

  const sideMat = wallMat;
  [-1, 1].forEach((side) => {
    const sideShape = new THREE.Shape();
    sideShape.moveTo(-projection / 2, 0);
    sideShape.lineTo(projection / 2, 0);
    sideShape.lineTo(projection / 2, dh);
    sideShape.lineTo(-projection / 2, dh);
    sideShape.lineTo(-projection / 2, 0);
    const sg = new THREE.ExtrudeGeometry(sideShape, { depth: 0.06, bevelEnabled: false });
    sg.computeVertexNormals();
    const sideMesh = new THREE.Mesh(sg, sideMat);
    sideMesh.castShadow = true;
    sideMesh.receiveShadow = true;
    sideMesh.rotation.y = Math.PI / 2;
    sideMesh.position.set(side * (w / 2), 0, 0);
    dormerGroup.add(sideMesh);
  });

  const dormerApexH = dh * 0.45;
  const ridgeY_d = dh + dormerApexH;
  const r1 = new THREE.Vector3(-w / 2, ridgeY_d - dormerApexH, projection / 2);
  const r2 = new THREE.Vector3(w / 2, ridgeY_d - dormerApexH, projection / 2);
  const apex = new THREE.Vector3(0, ridgeY_d, projection / 2);
  const back1 = new THREE.Vector3(-w / 2, dh, -projection / 2);
  const back2 = new THREE.Vector3(w / 2, dh, -projection / 2);
  const roofMat = materialFactory(roofMeta.roof || {});
  const buildTri = (a, b, c) => {
    const positions = new Float32Array([a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z]);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.computeVertexNormals();
    return g;
  };
  dormerGroup.add(new THREE.Mesh(buildTri(r1, apex, back1), roofMat));
  dormerGroup.add(new THREE.Mesh(buildTri(apex, r2, back2), roofMat));
  dormerGroup.add(new THREE.Mesh(buildTri(r1, back1, back2), roofMat));
  dormerGroup.add(new THREE.Mesh(buildTri(r1, back2, r2), roofMat));

  dormerGroup.position.copy(center);
  dormerGroup.rotation.y = yawRad;
  group.add(dormerGroup);
}

// ============================================================================
// Solar Panel (glossy dark-blue glass rectangle snapped to a roof slope).
// Mirrors the skylight geometry/placement logic but uses a photovoltaic look
// + thin aluminium frame. Supports arrays via `rows`/`cols` for multi-panel
// installs.
// ============================================================================
export function buildSolarPanel({ group, panel, roofMeta }) {
  const { type, longerIsX, span, ridgeLen, cx, cz, eaveY, pitchRad } = roofMeta;
  const face = String(panel.roof_face || (longerIsX ? "south" : "east")).toLowerCase();
  const u = Math.max(0, Math.min(1, Number(panel.position_u ?? 0.5)));
  const v = Math.max(0, Math.min(1, Number(panel.position_v ?? 0.5)));
  const pw = Number(panel.width || 1.6);
  const pl = Number(panel.length || 1.0);
  const rows = Math.max(1, Math.floor(Number(panel.rows || 1)));
  const cols = Math.max(1, Math.floor(Number(panel.cols || 1)));
  const gap = 0.05;
  const arrayW = cols * pw + (cols - 1) * gap;
  const arrayL = rows * pl + (rows - 1) * gap;

  const apexH = (span / 2) * Math.tan(pitchRad || 0);
  const ridgeY = eaveY + apexH;
  const half = ridgeLen / 2;

  let center = new THREE.Vector3();
  if (type === "flat") {
    center.set(cx, eaveY + 0.2, cz);
  } else if (type === "gable") {
    if (longerIsX) {
      const x = cx - half + u * ridgeLen;
      const sign = face === "south" ? 1 : -1;
      const z = cz + sign * (span / 2) * v;
      const y = ridgeY - apexH * v;
      center.set(x, y, z);
    } else {
      const z = cz - half + u * ridgeLen;
      const sign = face === "east" ? 1 : -1;
      const x = cx + sign * (span / 2) * v;
      const y = ridgeY - apexH * v;
      center.set(x, y, z);
    }
  } else {
    center.set(cx, ridgeY, cz);
  }

  // Dark photovoltaic glass + subtle grid (emulates cell pattern)
  const pvMat = new THREE.MeshPhysicalMaterial({
    color: 0x0a1e3a,
    roughness: 0.12,
    metalness: 0.2,
    clearcoat: 1.0,
    clearcoatRoughness: 0.08,
    side: THREE.DoubleSide,
  });
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x9aa3ad, roughness: 0.35, metalness: 0.8,
  });

  const panelGroup = new THREE.Group();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const px = -arrayW / 2 + c * (pw + gap) + pw / 2;
      const pz = -arrayL / 2 + r * (pl + gap) + pl / 2;
      // Cell grid lines — thin emissive stripes for a PV look
      const cellGeo = new THREE.PlaneGeometry(pw * 0.96, pl * 0.96);
      const cell = new THREE.Mesh(cellGeo, pvMat);
      cell.position.set(px, 0.005, pz);
      panelGroup.add(cell);
      // Aluminium frame as 4 thin bars
      const barT = 0.04;
      [
        { w: pw, d: barT, x: px, z: pz - pl / 2 + barT / 2 },
        { w: pw, d: barT, x: px, z: pz + pl / 2 - barT / 2 },
        { w: barT, d: pl, x: px - pw / 2 + barT / 2, z: pz },
        { w: barT, d: pl, x: px + pw / 2 - barT / 2, z: pz },
      ].forEach((b) => {
        const bar = new THREE.Mesh(
          new THREE.BoxGeometry(b.w, 0.03, b.d), frameMat,
        );
        bar.position.set(b.x, 0.018, b.z);
        panelGroup.add(bar);
      });
    }
  }
  // Lay flat (plane normal +Y) then tilt to match slope.
  if (type === "gable") {
    if (longerIsX) {
      const sign = face === "south" ? 1 : -1;
      panelGroup.rotation.x = sign * pitchRad;
    } else {
      const sign = face === "east" ? 1 : -1;
      panelGroup.rotation.z = -sign * pitchRad;
    }
  }
  panelGroup.position.copy(center);
  panelGroup.position.y += 0.05; // lift above roof surface
  group.add(panelGroup);
}

// ============================================================================
// Skylight (flat glass pane embedded in a roof slope)
// ============================================================================
export function buildSkylight({ group, skylight, roofMeta }) {
  const { type, longerIsX, span, ridgeLen, cx, cz, eaveY, pitchRad } = roofMeta;
  const face = String(skylight.roof_face || (longerIsX ? "south" : "east")).toLowerCase();
  const u = Math.max(0, Math.min(1, Number(skylight.position_u ?? 0.5)));
  const v = Math.max(0, Math.min(1, Number(skylight.position_v ?? 0.5)));
  const sw = Number(skylight.width || 1.2);
  const sl = Number(skylight.length || 1.6);

  const apexH = (span / 2) * Math.tan(pitchRad);
  const ridgeY = eaveY + apexH;
  const half = ridgeLen / 2;

  let center = new THREE.Vector3();

  if (type === "flat") {
    center.set(cx, eaveY + 0.2, cz);
  } else if (type === "gable") {
    if (longerIsX) {
      const x = cx - half + u * ridgeLen;
      if (face === "south") {
        const z = cz + (span / 2) * v;
        const y = ridgeY - apexH * v;
        center.set(x, y, z);
      } else {
        const z = cz - (span / 2) * v;
        const y = ridgeY - apexH * v;
        center.set(x, y, z);
      }
    } else {
      const z = cz - half + u * ridgeLen;
      if (face === "east") {
        const x = cx + (span / 2) * v;
        const y = ridgeY - apexH * v;
        center.set(x, y, z);
      } else {
        const x = cx - (span / 2) * v;
        const y = ridgeY - apexH * v;
        center.set(x, y, z);
      }
    }
  } else {
    center.set(cx, ridgeY, cz);
  }

  const glassMat = new THREE.MeshPhysicalMaterial({
    color: 0xa8d8f0, transparent: true, opacity: 0.55,
    roughness: 0.05, transmission: 0.7, metalness: 0.0,
    side: THREE.DoubleSide,
  });

  const glass = new THREE.Mesh(new THREE.PlaneGeometry(sw, sl), glassMat);
  if (type === "gable") {
    const sign = (face === "south" || face === "east") ? 1 : -1;
    if (longerIsX) {
      glass.rotation.x = -Math.PI / 2 + sign * pitchRad;
    } else {
      glass.rotation.x = -Math.PI / 2;
      glass.rotation.z = -sign * pitchRad;
      glass.rotation.y = Math.PI / 2;
    }
  } else if (type === "flat") {
    glass.rotation.x = -Math.PI / 2;
  }
  glass.position.copy(center);
  glass.position.y += 0.02;
  group.add(glass);

  const frame = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.PlaneGeometry(sw, sl)),
    new THREE.LineBasicMaterial({ color: 0x111111 }),
  );
  frame.rotation.copy(glass.rotation);
  frame.position.copy(glass.position);
  group.add(frame);
}

// ============================================================================
// Roof builder (gable / hip / flat)
// ============================================================================
export function buildRoof({ group, blueprint, bounds, eaveY, materialFactory }) {
  const roof = blueprint.roof;
  if (!roof) return null;
  const { minX, maxX, minZ, maxZ } = bounds;
  const overhang = Number(roof.overhang ?? 0.6);
  const roofW = (maxX - minX) + overhang * 2;
  const roofD = (maxZ - minZ) + overhang * 2;
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const pitchDeg = Number(roof.pitch_deg ?? 25);
  const pitchRad = (pitchDeg * Math.PI) / 180;
  const roofType = (roof.type || "gable").toLowerCase();
  const material = materialFactory(roof);

  if (roofType === "flat") {
    const slab = new THREE.Mesh(new THREE.BoxGeometry(roofW, 0.18, roofD), material);
    slab.position.set(cx, eaveY + 0.09, cz);
    slab.castShadow = true;
    slab.receiveShadow = true;
    group.add(slab);
    const parapetMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.9 });
    const t = 0.12;
    const ph = 0.45;
    [
      { w: roofW, d: t, x: cx, z: cz - roofD / 2 + t / 2 },
      { w: roofW, d: t, x: cx, z: cz + roofD / 2 - t / 2 },
      { w: t, d: roofD, x: cx - roofW / 2 + t / 2, z: cz },
      { w: t, d: roofD, x: cx + roofW / 2 - t / 2, z: cz },
    ].forEach((s) => {
      const p = new THREE.Mesh(new THREE.BoxGeometry(s.w, ph, s.d), parapetMat);
      p.position.set(s.x, eaveY + 0.18 + ph / 2, s.z);
      p.castShadow = true;
      p.receiveShadow = true;
      group.add(p);
    });
    return { type: "flat", longerIsX: roofW >= roofD, span: Math.min(roofW, roofD), ridgeLen: Math.max(roofW, roofD), cx, cz, eaveY, pitchRad: 0, roof, blueprint };
  }

  if (roofType === "hip") {
    const hx = roofW / 2;
    const hz = roofD / 2;
    const apexH = (Math.min(roofW, roofD) / 2) * Math.tan(pitchRad);
    const ridgeY = eaveY + apexH;
    const ridgeLen = Math.abs(roofW - roofD);

    let ridgeA, ridgeB;
    if (roofW >= roofD) {
      ridgeA = new THREE.Vector3(cx - ridgeLen / 2, ridgeY, cz);
      ridgeB = new THREE.Vector3(cx + ridgeLen / 2, ridgeY, cz);
    } else {
      ridgeA = new THREE.Vector3(cx, ridgeY, cz - ridgeLen / 2);
      ridgeB = new THREE.Vector3(cx, ridgeY, cz + ridgeLen / 2);
    }

    const c1 = new THREE.Vector3(cx - hx, eaveY, cz - hz);
    const c2 = new THREE.Vector3(cx + hx, eaveY, cz - hz);
    const c3 = new THREE.Vector3(cx + hx, eaveY, cz + hz);
    const c4 = new THREE.Vector3(cx - hx, eaveY, cz + hz);

    const faces = [
      [c1, c2, ridgeB, ridgeA],
      [c2, c3, ridgeB],
      [c3, c4, ridgeA, ridgeB],
      [c4, c1, ridgeA],
    ];
    faces.forEach((face) => {
      const verts = [];
      if (face.length === 3) verts.push(face[0], face[1], face[2]);
      else {
        verts.push(face[0], face[1], face[2]);
        verts.push(face[0], face[2], face[3]);
      }
      const positions = new Float32Array(verts.flatMap((v) => [v.x, v.y, v.z]));
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geo.computeVertexNormals();
      const m = new THREE.Mesh(geo, material);
      m.castShadow = true;
      m.receiveShadow = true;
      group.add(m);
    });
    return { type: "hip", longerIsX: roofW >= roofD, span: Math.min(roofW, roofD), ridgeLen: Math.max(roofW, roofD), cx, cz, eaveY, pitchRad, roof, blueprint };
  }

  // Gable (default) — explicit BufferGeometry quads, rotation-free.
  const longerIsX = roofW >= roofD;
  const span = longerIsX ? roofD : roofW;
  const ridgeLen = longerIsX ? roofW : roofD;
  const apexH = (span / 2) * Math.tan(pitchRad);
  const ridgeY = eaveY + apexH;

  const buildQuad = (a, b, c, d) => {
    const positions = new Float32Array([
      a.x, a.y, a.z,  b.x, b.y, b.z,  c.x, c.y, c.z,
      a.x, a.y, a.z,  c.x, c.y, c.z,  d.x, d.y, d.z,
    ]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.computeVertexNormals();
    return geo;
  };

  if (longerIsX) {
    const ridgeStart = new THREE.Vector3(cx - ridgeLen / 2, ridgeY, cz);
    const ridgeEnd   = new THREE.Vector3(cx + ridgeLen / 2, ridgeY, cz);
    const eaveSouthL = new THREE.Vector3(cx - ridgeLen / 2, eaveY, cz + span / 2);
    const eaveSouthR = new THREE.Vector3(cx + ridgeLen / 2, eaveY, cz + span / 2);
    const eaveNorthL = new THREE.Vector3(cx - ridgeLen / 2, eaveY, cz - span / 2);
    const eaveNorthR = new THREE.Vector3(cx + ridgeLen / 2, eaveY, cz - span / 2);
    const southGeo = buildQuad(eaveSouthL, eaveSouthR, ridgeEnd, ridgeStart);
    const northGeo = buildQuad(ridgeStart, ridgeEnd, eaveNorthR, eaveNorthL);
    [southGeo, northGeo].forEach((g) => {
      const m = new THREE.Mesh(g, material);
      m.castShadow = true;
      m.receiveShadow = true;
      group.add(m);
    });
  } else {
    const ridgeStart = new THREE.Vector3(cx, ridgeY, cz - ridgeLen / 2);
    const ridgeEnd   = new THREE.Vector3(cx, ridgeY, cz + ridgeLen / 2);
    const eaveEastF  = new THREE.Vector3(cx + span / 2, eaveY, cz - ridgeLen / 2);
    const eaveEastB  = new THREE.Vector3(cx + span / 2, eaveY, cz + ridgeLen / 2);
    const eaveWestF  = new THREE.Vector3(cx - span / 2, eaveY, cz - ridgeLen / 2);
    const eaveWestB  = new THREE.Vector3(cx - span / 2, eaveY, cz + ridgeLen / 2);
    const eastGeo = buildQuad(eaveEastF, eaveEastB, ridgeEnd, ridgeStart);
    const westGeo = buildQuad(ridgeStart, ridgeEnd, eaveWestB, eaveWestF);
    [eastGeo, westGeo].forEach((g) => {
      const m = new THREE.Mesh(g, material);
      m.castShadow = true;
      m.receiveShadow = true;
      group.add(m);
    });
  }

  // gable end caps anchored at the wall edge (not the overhang)
  const wallSpanX = maxX - minX;
  const wallSpanZ = maxZ - minZ;
  const capSpan = longerIsX ? wallSpanZ : wallSpanX;
  const capApexH = (capSpan / 2) * Math.tan(pitchRad);
  const capShape = new THREE.Shape();
  capShape.moveTo(-capSpan / 2, 0);
  capShape.lineTo(capSpan / 2, 0);
  capShape.lineTo(0, capApexH);
  capShape.lineTo(-capSpan / 2, 0);
  const capGeo = new THREE.ShapeGeometry(capShape);
  capGeo.computeVertexNormals();
  const farmhouse = isFarmhouseStyle(blueprint);
  const capMat = new THREE.MeshStandardMaterial({
    color: farmhouse ? 0xffffff : 0xeae0d3,
    roughness: 0.9,
    side: THREE.DoubleSide,
  });
  const cap1 = new THREE.Mesh(capGeo, capMat);
  const cap2 = new THREE.Mesh(capGeo, capMat);
  if (longerIsX) {
    cap1.position.set(cx - wallSpanX / 2, eaveY, cz);
    cap1.rotation.y = Math.PI / 2;
    cap2.position.set(cx + wallSpanX / 2, eaveY, cz);
    cap2.rotation.y = Math.PI / 2;
  } else {
    cap1.position.set(cx, eaveY, cz - wallSpanZ / 2);
    cap2.position.set(cx, eaveY, cz + wallSpanZ / 2);
  }
  group.add(cap1);
  group.add(cap2);
  // V1.5.2: Tag every roof mesh as exterior shell for the seamless-nav helpers.
  group.traverse((node) => {
    if (node.isMesh) {
      node.userData.kind = node.userData.kind || "roof";
      node.userData.isExteriorShell = true;
    }
  });
  return { type: "gable", longerIsX, span, ridgeLen, cx, cz, eaveY, pitchRad, roof, blueprint };
}

// ============================================================================
// Furniture library — basic GLTF-style placeholders built from primitives
// ============================================================================
export function buildFurniturePiece(f) {
  const group = new THREE.Group();
  const ghost = new THREE.MeshStandardMaterial({
    color: 0xffffff, transparent: true, opacity: 0.55, roughness: 0.45, metalness: 0.05,
  });
  const accent = new THREE.MeshStandardMaterial({
    color: 0xe0e6ec, transparent: true, opacity: 0.55, roughness: 0.5,
  });
  const edgeMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 });

  const type = String(f.type || "").toLowerCase();
  const size = f.size || [1, 1, 0.8];
  const sx = size[0] || 1;
  const sz = size[1] || 1;
  const sy = size[2] || 0.8;

  if (type === "table") {
    const topR = Math.min(sx, sz) / 2;
    const top = new THREE.Mesh(new THREE.CylinderGeometry(topR, topR, 0.05, 24), ghost);
    top.position.y = sy;
    group.add(top);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, sy, 12), ghost);
    leg.position.y = sy / 2;
    group.add(leg);
    const base = new THREE.Mesh(new THREE.CylinderGeometry(topR * 0.6, topR * 0.6, 0.05, 16), ghost);
    base.position.y = 0.025;
    group.add(base);
  } else if (type === "bed") {
    const platformH = 0.18;
    const matH = 0.22;
    const platform = new THREE.Mesh(new THREE.BoxGeometry(sx, platformH, sz), ghost);
    platform.position.y = platformH / 2;
    group.add(platform);
    const mattress = new THREE.Mesh(new THREE.BoxGeometry(sx * 0.96, matH, sz * 0.96), accent);
    mattress.position.y = platformH + matH / 2;
    group.add(mattress);
    const headboardH = 0.55;
    const headboard = new THREE.Mesh(new THREE.BoxGeometry(sx, headboardH, 0.05), ghost);
    headboard.position.set(0, platformH + headboardH / 2, -sz / 2);
    group.add(headboard);
  } else if (type === "sofa") {
    const seatH = 0.4;
    const seat = new THREE.Mesh(new THREE.BoxGeometry(sx, seatH, sz), ghost);
    seat.position.y = seatH / 2;
    group.add(seat);
    const cushH = 0.18;
    const cush = new THREE.Mesh(new THREE.BoxGeometry(sx * 0.96, cushH, sz * 0.85), accent);
    cush.position.set(0, seatH + cushH / 2, sz * 0.05);
    group.add(cush);
    const backH = 0.55;
    const back = new THREE.Mesh(new THREE.BoxGeometry(sx, backH, 0.18), ghost);
    back.position.set(0, seatH + backH / 2, -sz / 2 + 0.09);
    group.add(back);
  } else if (type === "chair") {
    const seatH = 0.45;
    const seat = new THREE.Mesh(new THREE.BoxGeometry(sx, 0.08, sz), ghost);
    seat.position.y = seatH;
    group.add(seat);
    const back = new THREE.Mesh(new THREE.BoxGeometry(sx, 0.55, 0.06), ghost);
    back.position.set(0, seatH + 0.275, -sz / 2 + 0.03);
    group.add(back);
    [-1, 1].forEach((dx) => {
      [-1, 1].forEach((dz) => {
        const leg = new THREE.Mesh(new THREE.BoxGeometry(0.05, seatH, 0.05), ghost);
        leg.position.set(dx * (sx / 2 - 0.04), seatH / 2, dz * (sz / 2 - 0.04));
        group.add(leg);
      });
    });
  } else if (type === "desk") {
    const topH = 0.05;
    const top = new THREE.Mesh(new THREE.BoxGeometry(sx, topH, sz), ghost);
    top.position.y = sy;
    group.add(top);
    [-1, 1].forEach((side) => {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.05, sy, sz), ghost);
      leg.position.set(side * (sx / 2 - 0.03), sy / 2, 0);
      group.add(leg);
    });
  } else {
    const box = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), ghost);
    box.position.y = sy / 2;
    group.add(box);
  }

  const outline = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.BoxGeometry(sx, sy, sz)),
    edgeMat,
  );
  outline.position.y = sy / 2;
  group.add(outline);

  group.children.forEach((c) => {
    c.castShadow = true;
    c.receiveShadow = true;
  });
  return group;
}

// ============================================================================
// Chimney — brick / stone / siding box that pierces the roof
// ============================================================================
export function buildChimney({ group, chimney, eaveY, ridgeY, textures }) {
  const w = Number(chimney.width || 0.7);
  const d = Number(chimney.depth || 0.7);
  const above = Number(chimney.height_above_roof || 1.4);
  const totalH = (ridgeY ?? eaveY + 2) + above;
  const mat = String(chimney.material || "brick").toLowerCase();
  const fallbackColor = { brick: 0x6e3a2c, stone: 0x8d8a7f, siding: 0xece4d3 };
  const tex = (textures && (textures[mat] || textures.brick)) || null;
  let material;
  if (tex) {
    const localTex = tex.clone();
    localTex.needsUpdate = true;
    localTex.repeat.set(Math.max(1, w * 1.2), Math.max(1, totalH * 0.6));
    material = new THREE.MeshStandardMaterial({
      map: localTex, color: 0xffffff, roughness: 0.85, metalness: 0.05,
    });
  } else {
    material = new THREE.MeshStandardMaterial({
      color: fallbackColor[mat] ?? 0x6e3a2c, roughness: 0.9,
    });
  }
  const box = new THREE.Mesh(new THREE.BoxGeometry(w, totalH, d), material);
  box.castShadow = true;
  box.receiveShadow = true;
  const pos = chimney.position || [0, 0];
  box.position.set(pos[0], totalH / 2, pos[1]);
  group.add(box);
  const cap = new THREE.Mesh(
    new THREE.BoxGeometry(w * 1.15, 0.1, d * 1.15),
    new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.9 }),
  );
  cap.position.set(pos[0], totalH + 0.05, pos[1]);
  cap.castShadow = true;
  group.add(cap);
}

// ============================================================================
// Deck / Porch — wood platform + optional railing
// ============================================================================
export function buildDeck({ group, deck, woodTexture }) {
  const w = Number(deck.width || 4);
  const l = Number(deck.length || 2.5);
  const h = Number(deck.height || 0.4);
  const pos = deck.position || [0, 0];
  const yaw = ((Number(deck.rotation_deg) || 0) * Math.PI) / 180;

  const grp = new THREE.Group();
  let platMat;
  if (woodTexture) {
    const platTex = woodTexture.clone();
    platTex.needsUpdate = true;
    platTex.repeat.set(Math.max(1, w / 1.2), Math.max(1, l / 1.2));
    platMat = new THREE.MeshStandardMaterial({
      map: platTex,
      color: deck.wood_color === "grey" ? 0xb6b3aa : (deck.wood_color === "painted" ? 0xf2e9d8 : 0xffffff),
      roughness: 0.85,
    });
  } else {
    platMat = new THREE.MeshStandardMaterial({
      color: deck.wood_color === "grey" ? 0x8a857a : (deck.wood_color === "painted" ? 0xf2e9d8 : 0x7a5a36),
      roughness: 0.9,
    });
  }
  const platform = new THREE.Mesh(new THREE.BoxGeometry(w, h, l), platMat);
  platform.castShadow = true;
  platform.receiveShadow = true;
  platform.position.y = h / 2;
  grp.add(platform);

  const rail = String(deck.railing || "none").toLowerCase();
  if (rail !== "none") {
    const railColor = rail === "white_wood" ? 0xfafafa : 0x111111;
    const railMat = new THREE.MeshStandardMaterial({
      color: railColor,
      roughness: rail === "white_wood" ? 0.85 : 0.45,
      metalness: rail === "black_metal" ? 0.7 : 0.0,
    });
    const railH = 1.0;
    const sides = [
      { x: 0, z: l / 2, w: w, d: 0.06, postZ: l / 2, wMode: "x" },
      { x: -w / 2, z: 0, w: 0.06, d: l, postX: -w / 2, wMode: "z" },
      { x: w / 2, z: 0, w: 0.06, d: l, postX: w / 2, wMode: "z" },
    ];
    sides.forEach((s) => {
      const top = new THREE.Mesh(new THREE.BoxGeometry(s.w, 0.05, s.d), railMat);
      top.position.set(s.x, h + railH, s.z);
      top.castShadow = true;
      grp.add(top);
      const postCount = 4;
      for (let i = 0; i <= postCount; i++) {
        const t = i / postCount;
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, railH, 0.06), railMat);
        if (s.wMode === "x") {
          post.position.set(-w / 2 + t * w, h + railH / 2, s.postZ);
        } else {
          post.position.set(s.postX, h + railH / 2, -l / 2 + t * l);
        }
        post.castShadow = true;
        grp.add(post);
      }
    });
  }

  grp.position.set(pos[0], 0, pos[1]);
  grp.rotation.y = yaw;
  group.add(grp);
}

// ============================================================================
// Walkway / Driveway — flat strip on the ground
// ============================================================================
export function buildWalkway({ group, walkway, textures }) {
  const start = walkway.start || [0, 0];
  const end = walkway.end || [0, 5];
  const dx = end[0] - start[0];
  const dz = end[1] - start[1];
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 0.1) return;
  const w = Number(walkway.width || (walkway.kind === "driveway" ? 3.2 : 1.2));
  const matName = String(walkway.material || "concrete").toLowerCase();
  const texMap = textures ? { concrete: textures.concrete, gravel: textures.concrete, brick: textures.brick } : {};
  const baseTex = texMap[matName];
  let mat;
  if (baseTex) {
    const tex = baseTex.clone();
    tex.needsUpdate = true;
    tex.repeat.set(Math.max(1, w * 0.8), Math.max(1, len * 0.5));
    mat = new THREE.MeshStandardMaterial({
      map: tex,
      color: matName === "gravel" ? 0xa5a098 : 0xffffff,
      roughness: 0.95,
    });
  } else {
    const colorByMat = { concrete: 0xa8a8a3, gravel: 0xa5a098, brick: 0x6e3a2c };
    mat = new THREE.MeshStandardMaterial({
      color: colorByMat[matName] ?? 0xa8a8a3,
      roughness: 0.95,
    });
  }
  const slab = new THREE.Mesh(new THREE.BoxGeometry(w, 0.05, len), mat);
  slab.receiveShadow = true;
  const cx = (start[0] + end[0]) / 2;
  const cz = (start[1] + end[1]) / 2;
  slab.position.set(cx, 0.025, cz);
  slab.rotation.y = -Math.atan2(dx, dz);
  group.add(slab);
}

// ============================================================================
// Tree — low-poly pine / oak / shrub
// ============================================================================
export function buildTree({ group, tree }) {
  const type = String(tree.type || "pine").toLowerCase();
  const h = Number(tree.height || (type === "shrub" ? 1.2 : 6.0));
  const r = Number(tree.radius || (type === "shrub" ? 0.6 : 1.2));
  const pos = tree.position || [0, 0];
  const grp = new THREE.Group();

  if (type === "pine") {
    const trunkH = h * 0.18;
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 0.18, r * 0.22, trunkH, 8),
      new THREE.MeshStandardMaterial({ color: 0x4a3322, roughness: 0.95 }),
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    grp.add(trunk);
    const layers = 3;
    const greens = [0x2f5d2a, 0x3a6f33, 0x478239];
    for (let i = 0; i < layers; i++) {
      const layerH = (h - trunkH) / layers * 1.4;
      const layerR = r * (1 - i * 0.18);
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(layerR, layerH, 8),
        new THREE.MeshStandardMaterial({ color: greens[i % greens.length], roughness: 0.9 }),
      );
      cone.position.y = trunkH + (i * (layerH * 0.55)) + layerH / 2;
      cone.castShadow = true;
      grp.add(cone);
    }
  } else if (type === "shrub") {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(r, 12, 8),
      new THREE.MeshStandardMaterial({ color: 0x3d6a35, roughness: 0.9 }),
    );
    sphere.position.y = r * 0.85;
    sphere.scale.y = 0.85;
    sphere.castShadow = true;
    grp.add(sphere);
  } else {
    const trunkH = h * 0.45;
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 0.18, r * 0.22, trunkH, 10),
      new THREE.MeshStandardMaterial({ color: 0x5a4126, roughness: 0.95 }),
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    grp.add(trunk);
    const greens = [0x3a6f33, 0x47823e, 0x55a046];
    for (let i = 0; i < 3; i++) {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(r * (0.85 + Math.random() * 0.2), 12, 8),
        new THREE.MeshStandardMaterial({ color: greens[i], roughness: 0.85 }),
      );
      const a = (i / 3) * Math.PI * 2;
      sphere.position.set(
        Math.cos(a) * r * 0.35,
        trunkH + r * 0.7 + Math.random() * 0.3,
        Math.sin(a) * r * 0.35,
      );
      sphere.castShadow = true;
      grp.add(sphere);
    }
  }
  grp.position.set(pos[0], 0, pos[1]);
  group.add(grp);
}

// ============================================================================
// V1.7 — Simple-Mode wall inference. The new "plain-talk" prompt returns
// rooms-only blueprints (rectangles with x/z/width/depth, walls: []). To
// give users a friendly 3D massing diagram on the same data, we infer the
// 4 perimeter walls of every room rectangle here. Duplicate walls between
// adjacent rooms are deduped via a position-keyed map so shared interior
// walls render exactly once.
// ============================================================================
function inferWallsFromRooms(rooms, ceilingHeight = 2.7) {
  if (!Array.isArray(rooms) || rooms.length === 0) return { walls: [], edgeIndex: new Map() };
  // V2.0.3 — also expose a public `edgeIndex` keyed by the canonical
  // edgeKey so the simple-schema door converter (room_a/room_b → wall_id)
  // can resolve which inferred wall a shared edge maps to.
  const edgeKey = (a, b) => {
    const k1 = `${a[0].toFixed(3)},${a[1].toFixed(3)}|${b[0].toFixed(3)},${b[1].toFixed(3)}`;
    const k2 = `${b[0].toFixed(3)},${b[1].toFixed(3)}|${a[0].toFixed(3)},${a[1].toFixed(3)}`;
    return k1 < k2 ? k1 : k2;
  };
  // V2.0.9 — merged-room support. The shared edge between two `merged_with`
  // rooms must NOT extrude as a wall in 3D — the user wants an L-shape
  // with no internal partition. We split each room's full edges against
  // merged-neighbour overlaps and emit ONE wall per remaining sub-segment.
  const isMerged = (a, b) => {
    const mA = Array.isArray(a.merged_with) ? a.merged_with : [];
    const mB = Array.isArray(b.merged_with) ? b.merged_with : [];
    return mA.includes(b.id) || mB.includes(a.id);
  };
  const valid = rooms.filter(
    (r) => r && Number.isFinite(r.x) && Number.isFinite(r.z) && r.width > 0 && r.depth > 0,
  );
  // Build "merged overlap" lookup: for each room edge, what sub-spans must
  // be subtracted because a merged neighbour shares that line?
  const mergedOverlapsFor = (r, edge) => {
    const out = [];
    valid.forEach((other) => {
      if (other === r || !isMerged(r, other)) return;
      const ox0 = other.x, ox1 = other.x + other.width;
      const oz0 = other.z, oz1 = other.z + other.depth;
      const otherEdges = edge.axis === "x"
        ? [
            { coord: oz0, span_lo: ox0, span_hi: ox1 },
            { coord: oz1, span_lo: ox0, span_hi: ox1 },
          ]
        : [
            { coord: ox0, span_lo: oz0, span_hi: oz1 },
            { coord: ox1, span_lo: oz0, span_hi: oz1 },
          ];
      otherEdges.forEach((oe) => {
        if (Math.abs(oe.coord - edge.coord) > 0.05) return;
        const lo = Math.max(edge.span_lo, oe.span_lo);
        const hi = Math.min(edge.span_hi, oe.span_hi);
        if (hi - lo > 0.05) out.push({ lo, hi });
      });
    });
    return out;
  };
  const walls = [];
  const edgeIndex = new Map();
  valid.forEach((r, i) => {
    const x0 = r.x, x1 = r.x + r.width;
    const z0 = r.z, z1 = r.z + r.depth;
    const edges = [
      { axis: "x", coord: z0, span_lo: x0, span_hi: x1 }, // bottom
      { axis: "z", coord: x1, span_lo: z0, span_hi: z1 }, // right
      { axis: "x", coord: z1, span_lo: x0, span_hi: x1 }, // top
      { axis: "z", coord: x0, span_lo: z0, span_hi: z1 }, // left
    ];
    edges.forEach((edge, j) => {
      let segs = [[edge.span_lo, edge.span_hi]];
      mergedOverlapsFor(r, edge).forEach(({ lo, hi }) => {
        const next = [];
        segs.forEach(([s, e]) => {
          if (hi <= s + 0.05 || lo >= e - 0.05) { next.push([s, e]); return; }
          if (lo - s > 0.05) next.push([s, lo]);
          if (e - hi > 0.05) next.push([hi, e]);
        });
        segs = next;
      });
      segs.forEach(([lo, hi], k) => {
        if (hi - lo < 0.1) return;
        const start = edge.axis === "x" ? [lo, edge.coord] : [edge.coord, lo];
        const end   = edge.axis === "x" ? [hi, edge.coord] : [edge.coord, hi];
        const key = edgeKey(start, end);
        if (edgeIndex.has(key)) return; // shared non-merged → emit once
        const wall = {
          id: `w_${r.id || i}_${j}_${k}`,
          start, end,
          height: ceilingHeight || 2.7,
          thickness: 0.18,
        };
        edgeIndex.set(key, wall);
        walls.push(wall);
      });
    });
  });
  return { walls, edgeIndex, edgeKey };
}

// V2.0.3 — Convert simple-schema interior doors ({ room_a, room_b, type })
// into the wall_id-based schema the rest of buildFloor consumes. We find
// the shared axis-aligned overlap between the two rooms and place the
// door at its midpoint. Returns a NEW array of doors with `wall_id` +
// `position` filled in. Doors marked `deleted: true` are skipped.
function convertRoomDoorsToWallDoors(doors, rooms, walls, edgeKey) {
  if (!Array.isArray(doors) || doors.length === 0) return doors || [];
  const byId = new Map((rooms || []).map((r) => [r.id, r]));
  const wallByEdge = new Map();
  (walls || []).forEach((w) => {
    if (!w.start || !w.end) return;
    wallByEdge.set(edgeKey(w.start, w.end), w);
  });
  const out = [];
  doors.forEach((d) => {
    if (!d) return;
    if (d.deleted === true) return;
    if (d.wall_id) { out.push(d); return; }
    // V2.0.7 — Exterior doors are stored window-style: room_a + side +
    // position. We resolve them to the EXTERIOR edge of the room (no
    // shared wall lookup), find the matching inferred wall, stamp
    // wall_id + position, and let the existing extruder render it.
    if (d.is_exterior && d.room_a && d.side) {
      const A = byId.get(d.room_a);
      if (!A) return;
      const side = String(d.side).toLowerCase();
      const Ax0 = A.x, Ax1 = A.x + A.width, Az0 = A.z, Az1 = A.z + A.depth;
      let segStart, segEnd;
      if (side === "front") {        segStart = [Ax0, Az0]; segEnd = [Ax1, Az0]; }
      else if (side === "rear")  {   segStart = [Ax0, Az1]; segEnd = [Ax1, Az1]; }
      else if (side === "left")  {   segStart = [Ax0, Az0]; segEnd = [Ax0, Az1]; }
      else {                          segStart = [Ax1, Az0]; segEnd = [Ax1, Az1]; }
      const tryKey = edgeKey(segStart, segEnd);
      let w = wallByEdge.get(tryKey);
      if (!w) return;
      // V2.0.16 — width default uses tolerant type-string matching.
      const _t1 = String(d.type || "").toLowerCase().trim().replace(/[\s_-]+/g, "_");
      const _wide1 = _t1 === "french" || _t1 === "french_door" || _t1 === "french_doors" || _t1 === "double"
        || _t1.startsWith("sliding_glass") || _t1 === "patio" || _t1 === "patio_door";
      out.push({
        ...d,
        wall_id: w.id,
        position: Number.isFinite(d.position) ? d.position : 0.5,
        width: Number.isFinite(d.width) ? d.width : (_wide1 ? 1.8 : 0.9),
        height: Number.isFinite(d.height) ? d.height : 2.1,
      });
      return;
    }
    if (!d.room_a || !d.room_b) return;
    const A = byId.get(d.room_a);
    const B = byId.get(d.room_b);
    if (!A || !B) return;
    // Shared edge: test the 4 axis-aligned configurations. For each side
    // of A, see if B's opposing side matches AND the perpendicular spans
    // overlap. The door centre = midpoint of the overlap.
    const Ax0 = A.x, Ax1 = A.x + A.width, Az0 = A.z, Az1 = A.z + A.depth;
    const Bx0 = B.x, Bx1 = B.x + B.width, Bz0 = B.z, Bz1 = B.z + B.depth;
    const EPS = 0.05;
    let shared = null; // { start: [x,z], end: [x,z], centre: [x,z] }
    // A.right == B.left  → vertical wall at x=Ax1, z overlap
    if (Math.abs(Ax1 - Bx0) < EPS) {
      const zLo = Math.max(Az0, Bz0); const zHi = Math.min(Az1, Bz1);
      if (zHi - zLo > 0.3) shared = { start: [Ax1, zLo], end: [Ax1, zHi] };
    }
    // A.left == B.right
    if (!shared && Math.abs(Ax0 - Bx1) < EPS) {
      const zLo = Math.max(Az0, Bz0); const zHi = Math.min(Az1, Bz1);
      if (zHi - zLo > 0.3) shared = { start: [Ax0, zLo], end: [Ax0, zHi] };
    }
    // A.top (rear) == B.bottom (front) → horizontal wall at z=Az1
    if (!shared && Math.abs(Az1 - Bz0) < EPS) {
      const xLo = Math.max(Ax0, Bx0); const xHi = Math.min(Ax1, Bx1);
      if (xHi - xLo > 0.3) shared = { start: [xLo, Az1], end: [xHi, Az1] };
    }
    // A.bottom == B.top
    if (!shared && Math.abs(Az0 - Bz1) < EPS) {
      const xLo = Math.max(Ax0, Bx0); const xHi = Math.min(Ax1, Bx1);
      if (xHi - xLo > 0.3) shared = { start: [xLo, Az0], end: [xHi, Az0] };
    }
    if (!shared) return;
    // Inferred walls are split per-room (each room contributes its 4
    // edges, but seen-map de-dupes by edgeKey). The wall whose endpoints
    // EQUAL the shared edge is the one we want. If the shared edge is a
    // SUB-segment of a larger wall, we still want that wall.
    const tryKey = edgeKey(shared.start, shared.end);
    let w = wallByEdge.get(tryKey);
    if (!w) {
      // Fall back: scan walls for the one parallel + colinear with the shared
      // segment and whose span includes it.
      w = (walls || []).find((wx) => {
        const a = wx.start, b = wx.end;
        if (!a || !b) return false;
        const horiz = Math.abs(a[1] - b[1]) < EPS;
        if (horiz) {
          if (Math.abs(a[1] - shared.start[1]) > EPS) return false;
          const lo = Math.min(a[0], b[0]); const hi = Math.max(a[0], b[0]);
          return shared.start[0] >= lo - EPS && shared.end[0] <= hi + EPS;
        }
        const vert = Math.abs(a[0] - b[0]) < EPS;
        if (!vert) return false;
        if (Math.abs(a[0] - shared.start[0]) > EPS) return false;
        const lo = Math.min(a[1], b[1]); const hi = Math.max(a[1], b[1]);
        return shared.start[1] >= lo - EPS && shared.end[1] <= hi + EPS;
      });
    }
    if (!w) return;
    // Compute position 0..1 along the wall's start→end axis to the
    // midpoint of the shared overlap.
    const midX = (shared.start[0] + shared.end[0]) / 2;
    const midZ = (shared.start[1] + shared.end[1]) / 2;
    const wx = w.end[0] - w.start[0], wz = w.end[1] - w.start[1];
    const wLen = Math.sqrt(wx * wx + wz * wz) || 1;
    const px = midX - w.start[0], pz = midZ - w.start[1];
    const t = Math.max(0.05, Math.min(0.95, (px * wx + pz * wz) / (wLen * wLen)));
    // V2.0.3 — also forward type, hinge, swing_direction, threshold and
    // any explicit width so the leaf renderer can emit type-distinct
    // geometry (french = 2 leaves, dutch = stacked panels, etc.).
    // V2.0.16 — width default with tolerant type matching.
    const _t2 = String(d.type || "").toLowerCase().trim().replace(/[\s_-]+/g, "_");
    const _wide2 = _t2 === "french" || _t2 === "french_door" || _t2 === "french_doors" || _t2 === "double"
      || _t2.startsWith("sliding_glass") || _t2 === "patio" || _t2 === "patio_door";
    out.push({
      ...d,
      wall_id: w.id,
      position: t,
      width: Number.isFinite(d.width) ? d.width : (_wide2 ? 1.8 : 0.9),
      height: Number.isFinite(d.height) ? d.height : 2.1,
    });
  });
  return out;
}

// ============================================================================
// Build a single floor (walls + windows + doors + slab + furniture)
// ============================================================================
export function buildFloor({
  floorGroup, floor, blueprint, sidingTexture, metalRoofTexture, style,
  staircaseHoles = [], isBasement = false, concreteTexture = null,
}) {
  let walls = floor.walls || [];
  // V2.0.3 — keep an edgeKey resolver so we can convert simple-schema
  // doors (room_a/room_b) into wall_id-based doors that the existing
  // extrusion + leaf path renders correctly.
  let edgeKeyFn = null;
  // V1.7 — Simple Mode fallback: when the prompt produced a rooms-only
  // blueprint (no explicit walls), auto-infer them from the room
  // rectangles so the 3D viewport still shows a proper massing diagram.
  if (walls.length === 0 && Array.isArray(floor.rooms) && floor.rooms.length > 0) {
    const inferred = inferWallsFromRooms(floor.rooms, floor.ceiling_height);
    walls = inferred.walls;
    edgeKeyFn = inferred.edgeKey;
  }
  const windowsArr = floor.windows || [];
  // V2.0.3 — Convert room_a/room_b doors → wall_id-based doors for 3D
  // rendering. The 2D editor stores doors in the simple schema; without
  // this conversion they'd never punch holes or render leaves.
  const rawDoors = floor.doors || [];
  const ek = edgeKeyFn || ((a, b) => {
    const k1 = `${a[0].toFixed(3)},${a[1].toFixed(3)}|${b[0].toFixed(3)},${b[1].toFixed(3)}`;
    const k2 = `${b[0].toFixed(3)},${b[1].toFixed(3)}|${a[0].toFixed(3)},${a[1].toFixed(3)}`;
    return k1 < k2 ? k1 : k2;
  });
  const doors = convertRoomDoorsToWallDoors(rawDoors, floor.rooms || [], walls, ek);
  const farmhouse = isFarmhouseStyle(blueprint, style);

  // Pre-compute footprint bounding box from all wall endpoints so we can
  // classify each wall as exterior (within ~0.4m of a bbox edge) vs interior
  // (everything else). This drives the "Show / Hide Interior Walls" toggle in
  // ViewOptionsPanel and keeps mansion-tier renders performant.
  let bboxMinX = Infinity, bboxMaxX = -Infinity;
  let bboxMinZ = Infinity, bboxMaxZ = -Infinity;
  walls.forEach((w) => {
    [w.start, w.end].forEach((p) => {
      if (!p) return;
      bboxMinX = Math.min(bboxMinX, p[0]);
      bboxMaxX = Math.max(bboxMaxX, p[0]);
      bboxMinZ = Math.min(bboxMinZ, p[1]);
      bboxMaxZ = Math.max(bboxMaxZ, p[1]);
    });
  });
  const EXT_TOL = 0.4;
  const isWallExterior = (w) => {
    if (!isFinite(bboxMinX)) return true;
    const start = w.start || [0, 0];
    const end = w.end || [1, 0];
    const mx = (start[0] + end[0]) / 2;
    const mz = (start[1] + end[1]) / 2;
    return (
      Math.abs(mx - bboxMinX) < EXT_TOL ||
      Math.abs(mx - bboxMaxX) < EXT_TOL ||
      Math.abs(mz - bboxMinZ) < EXT_TOL ||
      Math.abs(mz - bboxMaxZ) < EXT_TOL
    );
  };

  walls.forEach((w) => {
    const start = w.start || [0, 0];
    const end = w.end || [1, 0];
    const dx = end[0] - start[0];
    const dz = end[1] - start[1];
    const length = Math.sqrt(dx * dx + dz * dz);
    if (length < 0.01) return;
    const height = w.height || floor.ceiling_height || 3;
    const thickness = w.thickness || 0.2;
    const wallShape = new THREE.Shape();
    wallShape.moveTo(-length / 2, 0);
    wallShape.lineTo(length / 2, 0);
    wallShape.lineTo(length / 2, height);
    wallShape.lineTo(-length / 2, height);
    wallShape.lineTo(-length / 2, 0);

    windowsArr.filter((win) => win.wall_id === w.id).forEach((win) => {
      const pos = (win.position ?? 0.5) * length - length / 2;
      const ww = win.width || 1.2;
      const wh = win.height || 1.2;
      const sill = win.sill_height ?? 1.0;
      const hole = new THREE.Path();
      hole.moveTo(pos - ww / 2, sill);
      hole.lineTo(pos + ww / 2, sill);
      hole.lineTo(pos + ww / 2, sill + wh);
      hole.lineTo(pos - ww / 2, sill + wh);
      hole.lineTo(pos - ww / 2, sill);
      wallShape.holes.push(hole);
    });

    doors.filter((d) => d.wall_id === w.id).forEach((d) => {
      const pos = (d.position ?? 0.5) * length - length / 2;
      const dw = d.width || 0.9;
      const dh = d.height || 2.1;
      // V2.0.3 — threshold height: lift the BOTTOM of the punched hole
      // so the wall keeps a small lip beneath swing/single doors (~5mm
      // default — matches construction reality) and zero for
      // sliding_glass / patio sliders which sit flush with the floor.
      const tType = String(d.type || "swing").toLowerCase();
      const defaultThreshold = (tType === "sliding_glass" || tType === "sliding") ? 0 : 0.005;
      const threshold = Number.isFinite(d.threshold_height_m) ? d.threshold_height_m : defaultThreshold;
      const hole = new THREE.Path();
      hole.moveTo(pos - dw / 2, threshold);
      hole.lineTo(pos + dw / 2, threshold);
      hole.lineTo(pos + dw / 2, threshold + dh);
      hole.lineTo(pos - dw / 2, threshold + dh);
      hole.lineTo(pos - dw / 2, threshold);
      wallShape.holes.push(hole);
    });

    const geo = new THREE.ExtrudeGeometry(wallShape, { depth: thickness, bevelEnabled: false });
    const wallMat = buildWallMaterial({
      blueprint, wall: w, sidingTexture, style, isBasement, concreteTexture,
    });
    const mesh = new THREE.Mesh(geo, wallMat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const exterior = isWallExterior(w);
    mesh.userData.kind = "wall";
    mesh.userData.isExterior = exterior;
    // V1.5.2 Seamless Nav: tag exterior walls so the ghost-wall raycast +
    // dollhouse fade can target them without affecting interior partitions.
    if (exterior) mesh.userData.isExteriorShell = true;
    const cx = (start[0] + end[0]) / 2;
    const cz = (start[1] + end[1]) / 2;
    const angle = Math.atan2(dz, dx);
    mesh.position.set(cx, 0, cz);
    mesh.rotation.y = -angle;
    mesh.translateZ(-thickness / 2);
    floorGroup.add(mesh);

    windowsArr.filter((win) => win.wall_id === w.id).forEach((win) => {
      const pos = (win.position ?? 0.5) * length - length / 2;
      const ww = win.width || 1.2;
      const wh = win.height || 1.2;
      const sill = win.sill_height ?? 1.0;
      const pane = new THREE.Mesh(
        new THREE.PlaneGeometry(ww, wh),
        new THREE.MeshPhysicalMaterial({
          color: 0x88c0e0, transparent: true, opacity: 0.4,
          roughness: 0.05, transmission: 0.6, metalness: 0.0,
        }),
      );
      pane.position.set(cx, sill + wh / 2, cz);
      pane.rotation.y = -angle + Math.PI / 2;
      pane.position.x += Math.cos(angle) * pos;
      pane.position.z += Math.sin(angle) * pos;
      pane.userData.kind = "wall_window";
      pane.userData.isExterior = exterior;
      floorGroup.add(pane);
    });
  });

  // door leaves
  walls.forEach((w) => {
    const start = w.start || [0, 0];
    const end = w.end || [1, 0];
    const dx = end[0] - start[0];
    const dz = end[1] - start[1];
    const length = Math.sqrt(dx * dx + dz * dz);
    if (length < 0.01) return;
    const cx = (start[0] + end[0]) / 2;
    const cz = (start[1] + end[1]) / 2;
    const angle = Math.atan2(dz, dx);
    doors.filter((d) => d.wall_id === w.id).forEach((d) => {
      const pos = (d.position ?? 0.5) * length - length / 2;
      const dw = d.width || 0.9;
      const dh = d.height || 2.1;
      const matName = (d.material || "").toLowerCase();
      // V2.0.3 — type-distinct leaf geometry. Without this every door
      // looked like the same 0.04m-thick wood slab in 3D regardless of
      // whether the 2D plan said french / dutch / sliding_glass / etc.
      // V2.0.16 — normalise type strings so AI variants like "French",
      // "french-doors", "sliding-glass", "patio" match the same geometry
      // path as their canonical forms.
      const _rawType = String(d.type || "swing").toLowerCase().trim().replace(/[\s_-]+/g, "_");
      let tType = _rawType;
      if (_rawType === "french_door" || _rawType === "french_doors" || _rawType === "double") tType = "french";
      else if (_rawType === "sliding_glass_door" || _rawType === "sliding_glass_doors" || _rawType === "patio" || _rawType === "patio_door" || _rawType.startsWith("sliding_glass")) tType = "sliding_glass";
      else if (_rawType === "slider" || _rawType === "sliding_door" || _rawType === "sliding_doors") tType = "sliding";
      else if (_rawType === "pocket_door") tType = "pocket";
      else if (_rawType === "bi_fold" || _rawType === "bifold_door") tType = "bifold";
      const defaultThreshold = (tType === "sliding_glass" || tType === "sliding") ? 0 : 0.005;
      const threshold = Number.isFinite(d.threshold_height_m) ? d.threshold_height_m : defaultThreshold;
      const isGlassType = tType === "sliding_glass" || tType === "french";
      const palette = { wood: 0x6b4423, glass: 0x88c0e0, steel: 0x808890, metal: 0x808890 };
      const isGlassMat = matName === "glass" || isGlassType;
      const leafMat = new THREE.MeshStandardMaterial({
        color: palette[matName] ?? (isGlassType ? 0xa8c8d8 : 0x6b4423),
        roughness: isGlassMat ? 0.1 : 0.8,
        metalness: isGlassMat ? 0.0 : 0.05,
        transparent: isGlassMat,
        opacity: isGlassMat ? 0.45 : 1,
      });
      // Frame material for sliding_glass / french — slim aluminum stiles.
      const frameMat = new THREE.MeshStandardMaterial({
        color: 0x303336, roughness: 0.4, metalness: 0.6,
      });

      // Place the door group at the wall centre, then translate along the
      // wall axis by `pos`. Individual leaves position themselves relative
      // to this group origin.
      const doorGroup = new THREE.Group();
      doorGroup.userData.kind = "wall_door";
      doorGroup.userData.isExterior = isWallExterior(w);
      doorGroup.position.set(cx, threshold, cz);
      doorGroup.rotation.y = -angle + Math.PI / 2;
      doorGroup.position.x += Math.cos(angle) * pos;
      doorGroup.position.z += Math.sin(angle) * pos;

      const leafThk = 0.04;
      const halfDw = dw / 2;
      const interiorH = dh; // hole height above threshold

      if (tType === "french") {
        // Two side-by-side leaves, each ~dw/2 wide, slim mullion between.
        const leafW = (dw - 0.04) / 2;
        const leftLeaf = new THREE.Mesh(new THREE.BoxGeometry(leafW, interiorH, leafThk), leafMat);
        leftLeaf.position.set(-leafW / 2 - 0.02, interiorH / 2, 0);
        const rightLeaf = new THREE.Mesh(new THREE.BoxGeometry(leafW, interiorH, leafThk), leafMat);
        rightLeaf.position.set(leafW / 2 + 0.02, interiorH / 2, 0);
        const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.04, interiorH, leafThk + 0.005), frameMat);
        mullion.position.set(0, interiorH / 2, 0);
        [leftLeaf, rightLeaf, mullion].forEach((m) => { m.castShadow = true; m.receiveShadow = true; doorGroup.add(m); });
      } else if (tType === "dutch") {
        // Two stacked leaves split horizontally at ~55% of door height,
        // with a thin gap (the dutch-door divider).
        const splitY = interiorH * 0.55;
        const gap = 0.02;
        const bottomH = splitY - gap / 2;
        const topH = interiorH - splitY - gap / 2;
        const bottom = new THREE.Mesh(new THREE.BoxGeometry(dw, bottomH, leafThk), leafMat);
        bottom.position.set(0, bottomH / 2, 0);
        const top = new THREE.Mesh(new THREE.BoxGeometry(dw, topH, leafThk), leafMat);
        top.position.set(0, splitY + gap / 2 + topH / 2, 0);
        [bottom, top].forEach((m) => { m.castShadow = true; m.receiveShadow = true; doorGroup.add(m); });
      } else if (tType === "sliding_glass") {
        // Two thin overlapping glass panels (one ~5cm forward of wall plane,
        // one ~5cm rearward) inside an aluminum frame.
        const panelW = (dw - 0.03) / 2;
        const panelThk = 0.018;
        const left = new THREE.Mesh(new THREE.BoxGeometry(panelW, interiorH * 0.97, panelThk), leafMat);
        left.position.set(-panelW / 2 + 0.005, interiorH / 2, -0.012);
        const right = new THREE.Mesh(new THREE.BoxGeometry(panelW, interiorH * 0.97, panelThk), leafMat);
        right.position.set(panelW / 2 - 0.005, interiorH / 2, 0.012);
        // Aluminum top + bottom rails so the panel reads framed even with
        // glass transparency.
        const railTop = new THREE.Mesh(new THREE.BoxGeometry(dw, 0.04, panelThk + 0.01), frameMat);
        railTop.position.set(0, interiorH - 0.02, 0);
        const railBot = new THREE.Mesh(new THREE.BoxGeometry(dw, 0.04, panelThk + 0.01), frameMat);
        railBot.position.set(0, 0.02, 0);
        [left, right, railTop, railBot].forEach((m) => { m.castShadow = true; m.receiveShadow = true; doorGroup.add(m); });
      } else if (tType === "sliding") {
        // Two opaque panels overlapping in the wall plane.
        const panelW = dw * 0.55;
        const panelThk = 0.03;
        const left = new THREE.Mesh(new THREE.BoxGeometry(panelW, interiorH, panelThk), leafMat);
        left.position.set(-panelW / 2 + 0.03, interiorH / 2, -0.018);
        const right = new THREE.Mesh(new THREE.BoxGeometry(panelW, interiorH, panelThk), leafMat);
        right.position.set(panelW / 2 - 0.03, interiorH / 2, 0.018);
        [left, right].forEach((m) => { m.castShadow = true; m.receiveShadow = true; doorGroup.add(m); });
      } else if (tType === "bifold") {
        // Two pairs of half-panels, slightly folded toward the bigger room
        // (we use a tiny y-rotation as a visual hint of articulation).
        const panelW = dw / 4;
        const panelThk = 0.03;
        for (let i = 0; i < 4; i++) {
          const m = new THREE.Mesh(new THREE.BoxGeometry(panelW, interiorH, panelThk), leafMat);
          const base = -halfDw + (i + 0.5) * panelW;
          m.position.set(base, interiorH / 2, (i % 2 === 0 ? 1 : -1) * 0.015);
          m.rotation.y = (i % 2 === 0 ? 1 : -1) * 0.18;
          m.castShadow = true; m.receiveShadow = true;
          doorGroup.add(m);
        }
      } else {
        // Default: swing / single / dutch-fallback / pocket. Single slab.
        const leaf = new THREE.Mesh(new THREE.BoxGeometry(dw, interiorH, leafThk), leafMat);
        leaf.position.set(0, interiorH / 2, 0);
        leaf.castShadow = true;
        leaf.receiveShadow = true;
        doorGroup.add(leaf);
      }
      floorGroup.add(doorGroup);
    });
  });

  // slab bounds — reuse the bbox we computed above for the wall classifier.
  let minX = bboxMinX, maxX = bboxMaxX, minZ = bboxMinZ, maxZ = bboxMaxZ;
  if (!isFinite(minX)) { minX = -5; maxX = 5; minZ = -5; maxZ = 5; }
  if (walls.length > 0) {
    const declaredHoles = floor.floor_holes || [];
    const allHoles = [...declaredHoles, ...staircaseHoles];

    const sx = minX, sz = minZ, ex = maxX, ez = maxZ;
    const slabShape2 = new THREE.Shape();
    slabShape2.moveTo(sx, -sz);
    slabShape2.lineTo(ex, -sz);
    slabShape2.lineTo(ex, -ez);
    slabShape2.lineTo(sx, -ez);
    slabShape2.lineTo(sx, -sz);
    allHoles.forEach((h) => {
      const cx = (h.position && h.position[0]) || 0;
      const cz = (h.position && h.position[1]) || 0;
      const w = Number(h.width || 1.0);
      const l = Number(h.length || 4.0);
      const rot = ((Number(h.rotation_deg) || 0) * Math.PI) / 180;
      const corners = [
        [-w / 2, -l / 2],
        [w / 2, -l / 2],
        [w / 2, l / 2],
        [-w / 2, l / 2],
      ].map(([x, z]) => {
        const rx = x * Math.cos(rot) - z * Math.sin(rot);
        const rz = x * Math.sin(rot) + z * Math.cos(rot);
        return [cx + rx, -(cz + rz)];
      });
      const hole = new THREE.Path();
      hole.moveTo(corners[0][0], corners[0][1]);
      hole.lineTo(corners[1][0], corners[1][1]);
      hole.lineTo(corners[2][0], corners[2][1]);
      hole.lineTo(corners[3][0], corners[3][1]);
      hole.lineTo(corners[0][0], corners[0][1]);
      slabShape2.holes.push(hole);
    });

    const slabGeo = new THREE.ShapeGeometry(slabShape2);
    const slab = new THREE.Mesh(
      slabGeo,
      new THREE.MeshStandardMaterial({
        color: isBasement ? 0x5a5d60 : (farmhouse ? 0xe9dfcf : 0x3a3a3a),
        roughness: 0.9,
        side: THREE.DoubleSide,
      }),
    );
    slab.rotation.x = -Math.PI / 2;
    slab.position.y = 0.01;
    slab.receiveShadow = true;
    floorGroup.add(slab);
  }

  const furniture = floor.furniture || [];
  furniture.forEach((f) => {
    const piece = buildFurniturePiece(f);
    const pos = f.position || [0, 0];
    piece.position.set(pos[0], 0.02, pos[1]);
    piece.rotation.y = ((Number(f.rotation_deg) || 0) * Math.PI) / 180;
    floorGroup.add(piece);
  });

  return { bounds: { minX, maxX, minZ, maxZ } };
}

// ============================================================================
// Staircase primitive — sloped ramp with a stairs texture
// ============================================================================
export function buildStaircase({ stair, fromY, toY, stairsTexture }) {
  const length = Number(stair.length || 4);
  const width = Number(stair.width || 1);
  const rise = Math.max(0.5, toY - fromY);
  const slopeLen = Math.sqrt(length * length + rise * rise);
  const angle = Math.atan2(rise, length);

  const grp = new THREE.Group();
  const tex = stairsTexture.clone();
  tex.needsUpdate = true;
  tex.repeat.set(1, Math.max(2, Math.round(rise / 0.18)));
  const treadMat = new THREE.MeshStandardMaterial({
    map: tex, color: 0xffffff, roughness: 0.8, metalness: 0.05,
  });

  const ramp = new THREE.Mesh(new THREE.BoxGeometry(width, 0.08, slopeLen), treadMat);
  ramp.castShadow = true;
  ramp.receiveShadow = true;
  ramp.position.set(0, rise / 2 + fromY, 0);
  ramp.rotation.x = -angle;
  grp.add(ramp);

  const stringerMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.85 });
  [-1, 1].forEach((dx) => {
    const stringer = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, rise * 1.05, slopeLen * 0.95),
      stringerMat,
    );
    stringer.position.set(dx * (width / 2 + 0.02), rise / 2 + fromY, 0);
    stringer.rotation.x = -angle;
    stringer.castShadow = true;
    grp.add(stringer);
  });

  const pos = stair.position || [0, 0];
  grp.position.set(pos[0], 0, pos[1]);
  grp.rotation.y = ((Number(stair.rotation_deg) || 0) * Math.PI) / 180;
  return grp;
}
